import { useState } from 'react';
import { api } from '../../api/client.js';
import { ProductImage } from '../../components/ProductImage.js';
import {
  Alert,
  Field,
  PageHeader,
  StatusPill,
  fehlertext,
  useLoad,
} from '../shared/ui.js';

interface Article {
  id: string;
  articleNumber: string;
  description: string;
  unitOfMeasure: string;
  isActive: boolean;
  product: {
    id: string;
    shortName: string;
    typeValue: { code: string; imageRef: string | null };
    standardValue: { label: { de?: string } } | null;
  };
  materialValue: { id: string; code: string; label: { de?: string } };
  catalogVariant: { sizeLabel: string; pressureLabel: string | null } | null;
  _count: { priceListItems: number; quoteItems: number };
}
interface Wert {
  id: string;
  code: string;
  label: { de?: string };
  isActive: boolean;
  imageRef: string | null;
  sortOrder: number;
  _count: {
    productsAsType: number;
    articlesAsMaterial: number;
    deviations: number;
    overrides: number;
  };
}

/** Artikelstamm und Codetabellen. Nur Betreiber. Deaktivieren statt loeschen. */
export function ArticlesPage(): React.JSX.Element {
  const [tab, setTab] = useState<'artikel' | 'produkttypen' | 'werkstoffe'>('artikel');
  return (
    <>
      <PageHeader
        title="Artikel und Stammdaten"
        lead="Artikel, Produkttypen und Werkstoffe. Aenderungen wirken auf neue Auswahl und Angebote; historische Belege bleiben unveraendert."
      />
      <nav className="tabs">
        {(['artikel', 'produkttypen', 'werkstoffe'] as const).map((t) => (
          <button
            key={t}
            type="button"
            className={tab === t ? 'tab selected' : 'tab'}
            onClick={() => setTab(t)}
          >
            {
              {
                artikel: 'Artikel',
                produkttypen: 'Produkttypen',
                werkstoffe: 'Werkstoffe',
              }[t]
            }
          </button>
        ))}
      </nav>
      {tab === 'artikel' && <Artikel />}
      {tab === 'produkttypen' && <Werte attribute="product_type" titel="Produkttypen" />}
      {tab === 'werkstoffe' && <Werte attribute="material" titel="Werkstoffe" />}
    </>
  );
}

function Artikel(): React.JSX.Element {
  const [q, setQ] = useState('');
  const [suche, setSuche] = useState('');
  const [aktiv, setAktiv] = useState<'alle' | 'true' | 'false'>('alle');
  const params = new URLSearchParams({ limit: '100' });
  if (suche) params.set('q', suche);
  if (aktiv !== 'alle') params.set('active', aktiv);
  const { data, error, reload } = useLoad<{ items: Article[]; total: number }>(
    `/api/admin/articles?${params}`,
  );
  const [err, setErr] = useState<string | null>(null);
  const [neuOffen, setNeuOffen] = useState(false);
  const [bearbeite, setBearbeite] = useState<Article | null>(null);

  async function status(a: Article): Promise<void> {
    const frage = a.isActive
      ? `Artikel ${a.articleNumber} deaktivieren? Er verschwindet aus Katalog und Suche; ${a._count.quoteItems} historische Angebotspositionen bleiben unveraendert.`
      : `Artikel ${a.articleNumber} wieder aktivieren?`;
    if (!window.confirm(frage)) return;
    try {
      await api.patch(`/api/admin/articles/${a.id}`, { isActive: !a.isActive });
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }

  return (
    <>
      <form
        className="filterbar"
        onSubmit={(e) => {
          e.preventDefault();
          setSuche(q);
        }}
      >
        <input
          type="search"
          placeholder="Artikelnummer oder Bezeichnung"
          value={q}
          onChange={(e) => setQ(e.target.value)}
        />
        <button type="submit">Suchen</button>
        <select value={aktiv} onChange={(e) => setAktiv(e.target.value as typeof aktiv)}>
          <option value="alle">alle</option>
          <option value="true">nur aktive</option>
          <option value="false">nur inaktive</option>
        </select>
        <button type="button" className="primary" onClick={() => setNeuOffen(true)}>
          Artikel anlegen
        </button>
      </form>
      <Alert text={error ?? err} />
      {neuOffen && (
        <ArtikelNeu
          onDone={() => {
            setNeuOffen(false);
            reload();
          }}
          onCancel={() => setNeuOffen(false)}
        />
      )}
      {bearbeite && (
        <ArtikelBearbeiten
          a={bearbeite}
          onDone={() => {
            setBearbeite(null);
            reload();
          }}
          onCancel={() => setBearbeite(null)}
        />
      )}
      {data && (
        <div className="card">
          <p className="note">
            {data.total} Artikel{data.total > 100 && ', erste 100 gezeigt'}
          </p>
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th></th>
                  <th>Artikelnummer</th>
                  <th>Bezeichnung</th>
                  <th>Produkt</th>
                  <th>Werkstoff</th>
                  <th>Variante</th>
                  <th>Status</th>
                  <th className="num">Preise</th>
                  <th className="num">Angebote</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.items.map((a) => (
                  <tr key={a.id}>
                    <td className="thumb-zelle">
                      <ProductImage
                        imageRef={a.product.typeValue.imageRef}
                        variant="thumbnail"
                        label={a.product.shortName}
                      />
                    </td>
                    <td>
                      <code>{a.articleNumber}</code>
                    </td>
                    <td>{a.description}</td>
                    <td>
                      {a.product.typeValue.code} {a.product.shortName}
                    </td>
                    <td>
                      {a.materialValue.code} {a.materialValue.label?.de}
                    </td>
                    <td>
                      {a.catalogVariant ? (
                        `${a.catalogVariant.sizeLabel} ${a.catalogVariant.pressureLabel ?? ''}`
                      ) : (
                        <span className="muted">–</span>
                      )}
                    </td>
                    <td>
                      <StatusPill active={a.isActive} />
                    </td>
                    <td className="num">{a._count.priceListItems}</td>
                    <td className="num">{a._count.quoteItems}</td>
                    <td className="row">
                      <button
                        type="button"
                        className="link"
                        onClick={() => setBearbeite(a)}
                      >
                        bearbeiten
                      </button>
                      <button
                        type="button"
                        className="link"
                        onClick={() => void status(a)}
                      >
                        {a.isActive ? 'deaktivieren' : 'aktivieren'}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
}

function ArtikelNeu({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const produkte = useLoad<{
    items: Array<{ id: string; shortName: string; typeValue: { code: string } }>;
  }>('/api/admin/master/products');
  const werkstoffe = useLoad<{ items: Wert[] }>('/api/admin/master/material');
  const [f, setF] = useState({
    articleNumber: '',
    productId: '',
    materialValueId: '',
    description: '',
    unitOfMeasure: 'Stueck',
  });
  const [err, setErr] = useState<string | null>(null);
  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      await api.post('/api/admin/articles', f);
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  return (
    <section className="card highlight">
      <h3>Artikel anlegen</h3>
      <form onSubmit={speichern} className="grid2">
        <Field label="Artikelnummer" hint="Siebenstellig nach K+Z-Schema.">
          <input
            required
            value={f.articleNumber}
            onChange={(e) => setF({ ...f, articleNumber: e.target.value })}
          />
        </Field>
        <Field label="Bezeichnung">
          <input
            required
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
        <Field label="Produkt">
          <select
            required
            value={f.productId}
            onChange={(e) => setF({ ...f, productId: e.target.value })}
          >
            <option value="">– waehlen –</option>
            {(produkte.data?.items ?? []).map((p) => (
              <option key={p.id} value={p.id}>
                {p.typeValue.code} – {p.shortName}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Werkstoff">
          <select
            required
            value={f.materialValueId}
            onChange={(e) => setF({ ...f, materialValueId: e.target.value })}
          >
            <option value="">– waehlen –</option>
            {(werkstoffe.data?.items ?? [])
              .filter((w) => w.isActive)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.code} – {w.label?.de}
                </option>
              ))}
          </select>
        </Field>
        <Field label="Einheit">
          <input
            value={f.unitOfMeasure}
            onChange={(e) => setF({ ...f, unitOfMeasure: e.target.value })}
          />
        </Field>
        <div className="row" style={{ alignSelf: 'end' }}>
          <button type="submit" className="primary">
            Anlegen
          </button>
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
      <Alert text={err} />
      <p className="note">
        Ein Preis wird getrennt gepflegt: unter Preislisten, Einzelpreise. Ohne Preis
        erscheint der Artikel im Katalog, ist aber nicht bepreisbar.
      </p>
    </section>
  );
}

function ArtikelBearbeiten({
  a,
  onDone,
  onCancel,
}: {
  a: Article;
  onDone: () => void;
  onCancel: () => void;
}): React.JSX.Element {
  const [f, setF] = useState({
    description: a.description,
    unitOfMeasure: a.unitOfMeasure,
  });
  const [err, setErr] = useState<string | null>(null);
  async function speichern(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    setErr(null);
    try {
      await api.patch(`/api/admin/articles/${a.id}`, f);
      onDone();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  return (
    <section className="card highlight">
      <h3>Artikel {a.articleNumber} bearbeiten</h3>
      <form onSubmit={speichern} className="grid2">
        <Field label="Bezeichnung">
          <input
            required
            value={f.description}
            onChange={(e) => setF({ ...f, description: e.target.value })}
          />
        </Field>
        <Field label="Einheit">
          <input
            value={f.unitOfMeasure}
            onChange={(e) => setF({ ...f, unitOfMeasure: e.target.value })}
          />
        </Field>
        <div className="row" style={{ alignSelf: 'end' }}>
          <button type="submit" className="primary">
            Speichern
          </button>
          <button type="button" onClick={onCancel}>
            Abbrechen
          </button>
        </div>
      </form>
      <Alert text={err} />
      <p className="note">
        Produkt und Werkstoff sind Teil der Artikelidentitaet und nicht aenderbar.
        Historische Angebote tragen Bezeichnung und Nummer als Kopie und aendern sich
        nicht.
      </p>
    </section>
  );
}

function Werte({
  attribute,
  titel,
}: {
  attribute: 'product_type' | 'material';
  titel: string;
}): React.JSX.Element {
  const { data, error, reload } = useLoad<{ items: Wert[]; imageRefs: string[] }>(
    `/api/admin/master/${attribute}`,
  );
  const [err, setErr] = useState<string | null>(null);
  async function patch(id: string, body: object): Promise<void> {
    setErr(null);
    try {
      await api.patch(`/api/admin/master/values/${id}`, body);
      reload();
    } catch (x) {
      setErr(fehlertext(x));
    }
  }
  return (
    <section className="card">
      <h3>{titel}</h3>
      <p className="note">
        Inaktive Werte sind kein Fehler: Sie sind bekannte K+Z-Codes, die nicht zum
        V1-Sortiment gehoeren. Sie werden in Katalog, Suche und Abweichungen nicht
        angeboten. Historische Verweise bleiben erhalten.
      </p>
      <Alert text={error ?? err} />
      <div className="table-scroll">
        <table>
          <thead>
            <tr>
              <th>Code</th>
              <th>Klartext</th>
              <th>Status</th>
              {attribute === 'product_type' && (
                <>
                  <th>Schnittbild</th>
                  <th className="num">Produkte</th>
                </>
              )}
              {attribute === 'material' && <th className="num">Artikel</th>}
              <th className="num">Abweichungen</th>
            </tr>
          </thead>
          <tbody>
            {(data?.items ?? []).map((w) => (
              <tr key={w.id}>
                <td>
                  <code>{w.code}</code>
                </td>
                <td>
                  <input
                    defaultValue={w.label?.de ?? ''}
                    onBlur={(e) =>
                      e.target.value !== (w.label?.de ?? '') &&
                      void patch(w.id, { label: e.target.value })
                    }
                  />
                </td>
                <td>
                  <button
                    type="button"
                    className="link"
                    onClick={() => void patch(w.id, { isActive: !w.isActive })}
                  >
                    <StatusPill
                      active={w.isActive}
                      activeText="im Sortiment"
                      inactiveText="nicht im V1-Sortiment"
                    />
                  </button>
                </td>
                {attribute === 'product_type' && (
                  <>
                    <td className="row" style={{ alignItems: 'center' }}>
                      <ProductImage
                        imageRef={w.imageRef}
                        variant="thumbnail"
                        label={w.code}
                      />
                      <select
                        value={w.imageRef ?? ''}
                        onChange={(e) =>
                          void patch(w.id, {
                            imageRef: e.target.value === '' ? null : e.target.value,
                          })
                        }
                      >
                        <option value="">– kein Bild –</option>
                        {(data?.imageRefs ?? []).map((r) => (
                          <option key={r} value={r}>
                            {r}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="num">{w._count.productsAsType}</td>
                  </>
                )}
                {attribute === 'material' && (
                  <td className="num">{w._count.articlesAsMaterial}</td>
                )}
                <td className="num">{w._count.deviations + w._count.overrides}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {attribute === 'product_type' && (
        <p className="note">
          Schnittbilder liegen zentral als Assets. Ein Upload neuer Bilder ist in V1 nicht
          vorgesehen; Auswahl aus den vorhandenen Dateien.
        </p>
      )}
    </section>
  );
}
